/**
 * Scrolls the inventory row for a specific variant into view when the user
 * arrives from the cart. Triggered by the `scrollToVariant=1` query flag that
 * the cart appends to its line-item edit links.
 *
 * The URL already carries `?variant={id}` (Shopify's default line_item.url);
 * the flag is what distinguishes a cart click from other deep links (search,
 * bookmarks) where auto-scrolling would be unwanted. The flag is stripped from
 * the URL after handling so a refresh or share doesn't retrigger the behavior.
 */

const FLAG_PARAM = "scrollToVariant";
const VARIANT_PARAM = "variant";
const HIGHLIGHT_CLASS = "scrollto-variant-highlight";
const MAX_WAIT_MS = 3000;
const POLL_INTERVAL_MS = 100;
const STICKY_GAP_PX = 8;

export async function scrollToVariantFromUrl(): Promise<void> {
  const url = new URL(window.location.href);
  if (url.searchParams.get(FLAG_PARAM) !== "1") return;

  const variantId = url.searchParams.get(VARIANT_PARAM);

  url.searchParams.delete(FLAG_PARAM);
  window.history.replaceState(
    window.history.state,
    "",
    url.pathname + url.search + url.hash,
  );

  if (!variantId) return;

  const input = await waitForInput(variantId);
  if (!input) return;

  const row = input.closest(".warehouse_row") as HTMLElement | null;
  if (!row) return;

  row.style.scrollMarginTop = `${measureStickyOffset()}px`;
  row.scrollIntoView({ block: "start", behavior: "instant" });

  row.classList.remove(HIGHLIGHT_CLASS);
  void row.offsetWidth;
  row.classList.add(HIGHLIGHT_CLASS);
  row.addEventListener(
    "animationend",
    () => row.classList.remove(HIGHLIGHT_CLASS),
    { once: true },
  );

  if (isDesktopPointer()) {
    input.focus({ preventScroll: true });
  }
}

function waitForInput(variantId: string): Promise<HTMLInputElement | null> {
  const selector =
    `.variant_qty_input_directship[data-variant-id="${variantId}"],` +
    `.variant_qty_input_local[data-variant-id="${variantId}"]`;

  const immediate = document.querySelector<HTMLInputElement>(selector);
  if (immediate) return Promise.resolve(immediate);

  return new Promise((resolve) => {
    const start = Date.now();
    const timer = window.setInterval(() => {
      const el = document.querySelector<HTMLInputElement>(selector);
      if (el) {
        window.clearInterval(timer);
        resolve(el);
      } else if (Date.now() - start > MAX_WAIT_MS) {
        window.clearInterval(timer);
        resolve(null);
      }
    }, POLL_INTERVAL_MS);
  });
}

function measureStickyOffset(): number {
  const headerVar = getComputedStyle(document.documentElement)
    .getPropertyValue("--theme-sticky-header-height")
    .trim();
  const headerHeight = parseInt(headerVar, 10) || 0;

  const tableHeader = document.querySelector<HTMLElement>(".inventory_header_row");
  const tableHeaderHeight = tableHeader
    ? tableHeader.getBoundingClientRect().height
    : 0;

  return Math.ceil(headerHeight + tableHeaderHeight + STICKY_GAP_PX);
}

function isDesktopPointer(): boolean {
  return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
}
